// MailClientModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Space, Typography, Divider, Alert, App } from "antd";
import { CopyOutlined, MailOutlined, CheckCircleOutlined, DownloadOutlined } from "@ant-design/icons";

const { Text } = Typography;

const MAIL_CLIENTS = [
  {
    id: "mailto",
    label: "Standard Mail-App",
    icon: <MailOutlined />,
    description: "Outlook, Apple Mail, Thunderbird …",
    getUrl: ({ recipient, subject, body }) =>
      `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
    opensNewTab: false,
  },
  {
    id: "seminar-bw",
    label: "mail.seminar-bw.de",
    icon: <MailOutlined />,
    description: "Login erforderlich (keine automatische Mail möglich)",
    getUrl: () => `https://mail.seminar-bw.de/`,
    opensNewTab: true,
  },
 
];

// Innere Komponente – nutzt App.useApp() für message
const MailClientModalInner = ({ open, onClose, blob, fileName, recipient = "", subject = "", body = "" }) => {
  // ✅ Ant Design v5: message nur über App.useApp() verwenden
  const { message } = App.useApp();

  const [downloaded, setDownloaded] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [seminarConfirmOpen, setSeminarConfirmOpen] = useState(false);
  const [seminarUrl, setSeminarUrl] = useState("");

 const copyToClipboard = async (text, label) => {
  // Moderner Weg (HTTPS + unterstützter Browser)
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${label} kopiert`, 2);
      return;
    } catch (e) {
      // fällt durch zum Fallback
    }
  }

  // Fallback für mobile Browser / HTTP
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none;";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const success = document.execCommand("copy");
    document.body.removeChild(textarea);

    if (success) {
      message.success(`${label} kopiert`, 2);
    } else {
      throw new Error("execCommand failed");
    }
  } catch (e) {
    message.error("Kopieren fehlgeschlagen");
  }
};

  useEffect(() => {
    if (open && blob) {
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      setDownloaded(false);
    }
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [open, blob]);

   // Download per direktem User-Gesture (Klick auf Button)
  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setDownloaded(true);
  };

  const handleMailClient = (client) => {
    const url = client.getUrl({ recipient, subject, body });
    if (client.id === "seminar-bw") {
      setSeminarUrl(url);
      setSeminarConfirmOpen(true);
      return;
    }
    if (client.opensNewTab) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = url;
    }
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        title={<Space><MailOutlined /><span>PDF versenden</span></Space>}
        width={480}
      >
        <div style={{ marginBottom: 16 }}>
  {downloaded ? (
    <Alert
      type="success"
      showIcon
      icon={<CheckCircleOutlined />}
      message={<span>PDF heruntergeladen: <Text strong>{fileName}</Text></span>}
      description={
        <a onClick={handleDownload} style={{ cursor: "pointer" }}>
          Erneut herunterladen
        </a>
      }
    />
  ) : (
    <Button
      type="primary"
      icon={<DownloadOutlined />}
      block
      size="large"
      onClick={handleDownload}
      disabled={!blobUrl}
      style={{ marginBottom: 4 }}
    >
      PDF herunterladen
    </Button>
  )}
</div>

        <Divider style={{ margin: "12px 0" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Mail-Programm öffnen</Text>
        </Divider>

        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Hinweis: Die PDF-Datei muss manuell als Anhang aus dem Download-Ordner hinzugefügt werden."
        />

        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          {MAIL_CLIENTS.map((client) => (
            <Button
              key={client.id}
              block
              size="large"
              icon={<span style={{ fontSize: 18 }}>{client.icon}</span>}
              onClick={() => handleMailClient(client)}
              style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 12, height: 52, paddingLeft: 16 }}
            >
              <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.3 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{client.label}</span>
                <span style={{ fontSize: 11, color: "#888", fontWeight: 400 }}>{client.description}</span>
              </span>
            </Button>
          ))}
        </Space>

        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Button onClick={onClose}>Schließen</Button>
        </div>
      </Modal>

      <Modal
        open={seminarConfirmOpen}
        onCancel={() => setSeminarConfirmOpen(false)}
        onOk={() => { window.open(seminarUrl, "_blank", "noopener,noreferrer"); setSeminarConfirmOpen(false); }}
        title="https://mail.seminar-bw.de öffnen?"
        okText="Login-Seite öffnen"
        cancelText="Abbrechen"
      >
        <p>Nach dem Login bitte:</p>
        <ol>
          <li>„Neue Mail" klicken</li>
          <li>Empfänger einfügen</li>
          <li>PDF anhängen</li>
        </ol>

        <Divider />

        <Space direction="vertical" style={{ width: "100%" }}>
          <p>Empfänger:</p>
          <div
            onClick={() => copyToClipboard(recipient, "Empfänger")}
            style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "2px 0", color: "#1677ff" }}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
          >
            <CopyOutlined style={{ fontSize: 12, opacity: 0.6 }} />
            <span style={{ userSelect: "none" }}>haushalt@seminar-gym-es.kv.bwl.de</span>
          </div>
        </Space>
      </Modal>
    </>
  );
};

// Äußere Komponente stellt den App-Context bereit, damit useApp() funktioniert
const MailClientModal = (props) => (
  <App>
    <MailClientModalInner {...props} />
  </App>
);

export default MailClientModal;
